/**
 * Visual distinction for fenced code blocks in the source editor.
 *
 * Busy docs interleave prose with many embedded scripts, and in a flat
 * monospace view it's hard to see at a glance where text ends and code
 * begins. Two complementary cues:
 *
 * 1. `codeBlockHighlight()` — line decorations giving every fenced code
 *    block (``` or ~~~) a darker background and a bordered box (orange
 *    accent bar on the left, 1px edge on the other sides via inset
 *    box-shadows, so line layout never shifts). Fence lines carry
 *    `cm-codeblock-fence-open` / `cm-codeblock-fence-close` (top/bottom
 *    edges), body lines `cm-codeblock-line` (styled in styles.css).
 *
 * 2. `codeBlockLineNumbers()` — a gutter that numbers ONLY code-block body
 *    lines, restarting at 1 for every block (the absolute document numbers
 *    from basicSetup's gutter are hidden in the main editor via CSS: line
 *    numbers matter when eyeballing a script, not prose). Fence and prose
 *    lines get no number.
 *
 * Decorations are rebuilt on doc/viewport changes and only emitted for the
 * visible viewport; the per-line number labels are memoized per document,
 * so large documents stay cheap.
 */

import {
  Decoration,
  EditorView,
  ViewPlugin,
  gutter,
  GutterMarker,
  type DecorationSet,
  type ViewUpdate,
} from '@codemirror/view';
import { RangeSetBuilder, type EditorState, type Extension, type Text } from '@codemirror/state';
import { scanCodeBlocks } from './code-block-edit.js';

// ---------- Background tint + bordered box ----------

const fenceOpenLine = Decoration.line({ class: 'cm-codeblock-fence cm-codeblock-fence-open' });
const fenceCloseLine = Decoration.line({ class: 'cm-codeblock-fence cm-codeblock-fence-close' });
const bodyLine = Decoration.line({ class: 'cm-codeblock-line' });

function buildDecorations(view: EditorView): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  const { doc } = view.state;
  const firstVisible = doc.lineAt(view.viewport.from).number;
  const lastVisible = doc.lineAt(view.viewport.to).number;

  for (const block of scanCodeBlocks(view.state)) {
    if (block.closeLineNo < firstVisible) continue;
    if (block.openLineNo > lastVisible) break;
    const from = Math.max(block.openLineNo, firstVisible);
    const to = Math.min(block.closeLineNo, lastVisible);
    for (let n = from; n <= to; n++) {
      const line = doc.line(n);
      const deco =
        n === block.openLineNo ? fenceOpenLine :
        n === block.closeLineNo ? fenceCloseLine :
        bodyLine;
      builder.add(line.from, line.from, deco);
    }
  }

  return builder.finish();
}

/**
 * Create the code-block highlight extension: darker background + bordered
 * box (left accent bar) on every fenced code block's lines.
 */
export function codeBlockHighlight(): Extension {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = buildDecorations(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = buildDecorations(update.view);
        }
      }
    },
    { decorations: (v) => v.decorations }
  );
}

// ---------- Per-block restarting line numbers ----------

interface BlockNumbering {
  /** 1-based document line number → label ("1", "2", … within its block). */
  labels: Map<number, string>;
  /** Widest label, for sizing the gutter. */
  spacer: string;
}

const numberingCache = new WeakMap<Text, BlockNumbering>();

function blockNumbering(state: EditorState): BlockNumbering {
  const cached = numberingCache.get(state.doc);
  if (cached) return cached;

  const labels = new Map<number, string>();
  let maxDigits = 1;
  for (const block of scanCodeBlocks(state)) {
    let rel = 1;
    for (let n = block.openLineNo + 1; n < block.closeLineNo; n++, rel++) {
      labels.set(n, String(rel));
    }
    maxDigits = Math.max(maxDigits, String(Math.max(1, rel - 1)).length);
  }

  const numbering: BlockNumbering = { labels, spacer: '9'.repeat(maxDigits) };
  numberingCache.set(state.doc, numbering);
  return numbering;
}

class NumberMarker extends GutterMarker {
  constructor(readonly label: string) {
    super();
  }

  override eq(other: NumberMarker): boolean {
    return other.label === this.label;
  }

  override toDOM(): Node {
    return document.createTextNode(this.label);
  }
}

/**
 * Create the per-code-block line-number gutter: body lines of every fenced
 * code block are numbered 1..N, restarting for each block; fence and prose
 * lines show nothing.
 */
export function codeBlockLineNumbers(): Extension {
  return gutter({
    class: 'cm-codeblock-linenos',
    lineMarker(view, line) {
      const lineNo = view.state.doc.lineAt(line.from).number;
      const label = blockNumbering(view.state).labels.get(lineNo);
      return label ? new NumberMarker(label) : null;
    },
    lineMarkerChange: (update) => update.docChanged,
    initialSpacer: (view) => new NumberMarker(blockNumbering(view.state).spacer),
    updateSpacer: (spacer, update) => {
      const next = blockNumbering(update.view.state).spacer;
      return (spacer as NumberMarker).label === next ? spacer : new NumberMarker(next);
    },
  });
}
